/**
 * DeviceStatusTile — "what's alive at this box right now", step 0 of the
 * troubleshooting walkthrough.
 *
 * Answers the first question a tech actually asks when they pick up a
 * maintenance ticket: which pieces of kit are up? Peplink, smartplug, Starlink,
 * UPS/APC, Tailscale, the RFID scanner, and the camera — one glance, before any
 * of the remote/local diagnostic steps.
 *
 * DESIGN NOTES
 *
 * Mobile-first, because the reader is standing at a site holding a phone:
 * one row per device (never a horizontal table that needs scrolling), large
 * tap targets, and the status word repeated in text rather than relying on the
 * dot colour alone (sunlight on a phone screen, and colour-blind readers).
 *
 * Signal derivation is NOT duplicated here. Peplink/plug/UPS/Tailscale/Starlink
 * all come from `deriveSignalDots(box)` — the same helper Boxes, Outages, Map
 * and SignalDetail use — so this tile can never disagree with the map bubble
 * about the same box. Reimplementing the thresholds locally is how those two
 * screens would drift.
 *
 * Scanner and camera are NOT in the snapshot (they're on-demand per box), so
 * they load lazily: the five snapshot signals paint immediately, and these two
 * fill in when their fetches land. A tech on bad site LTE sees the important
 * state instantly instead of staring at a spinner.
 */
import React, { useEffect, useMemo, useState } from "react";

import SignalDot, { type DotStatus } from "../SignalDot";
import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo } from "../../lib/format";
import { deriveSignalDots } from "../../lib/signalDots";
import {
  type BoxPhoto,
  type BoxScanDetail,
  fetchPhotos,
  fetchScanDetail,
} from "../../services/boxDetailApi";
import type { Box } from "../../types/boxes";

interface DeviceStatusTileProps {
  box: Box;
}

interface Row {
  key: string;
  label: string;
  status: DotStatus | null;
  /** Short state text, e.g. "online 4m" — shown next to the dot. */
  value: string;
  /** Longer explanation; the tooltip on desktop, the second line on mobile. */
  detail: string;
  /** True while an on-demand fetch for this row is still in flight. */
  pending?: boolean;
}

/** How stale a scan may be before it reads as a problem. */
const SCAN_WARN_MIN = 60;
const SCAN_BAD_MIN = 360; // 6h — well past any normal scan cadence
/** Same idea for the camera. */
const PHOTO_WARN_MIN = 180;
const PHOTO_BAD_MIN = 1440; // 24h

function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

function freshnessStatus(
  mins: number | null,
  warnMin: number,
  badMin: number,
): DotStatus {
  if (mins == null) return "unknown";
  if (mins >= badMin) return "bad";
  if (mins >= warnMin) return "warn";
  return "ok";
}

export const DeviceStatusTile: React.FC<DeviceStatusTileProps> = ({ box }) => {
  const { token } = useAuth();
  const [scan, setScan] = useState<BoxScanDetail | null>(null);
  const [photo, setPhoto] = useState<BoxPhoto | null>(null);
  const [scanLoading, setScanLoading] = useState(true);
  const [photoLoading, setPhotoLoading] = useState(true);
  const [scanError, setScanError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const scu = box.scu_name;

  // Lazy loads. Deliberately two independent effects, not one Promise.all:
  // a camera outage must not stop the scanner row from filling in, which is
  // exactly the situation this tile exists to diagnose.
  useEffect(() => {
    if (!token || !scu) return;
    let cancelled = false;
    setScanLoading(true);
    setScanError(null);
    fetchScanDetail(scu, token)
      .then((d) => {
        if (!cancelled) setScan(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setScanError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setScanLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scu, token]);

  useEffect(() => {
    if (!token || !scu) return;
    let cancelled = false;
    setPhotoLoading(true);
    setPhotoError(null);
    // limit=1: we only need "when was the last photo", not a gallery.
    fetchPhotos(scu, token, { limit: 1 })
      .then((d) => {
        if (!cancelled) setPhoto(d.photos?.[0] ?? null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPhotoError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setPhotoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scu, token]);

  const rows: Row[] = useMemo(() => {
    const d = deriveSignalDots(box);
    const out: Row[] = [
      { key: "pep", label: "Peplink", status: d.pep.status, value: d.pep.inline, detail: d.pep.title },
      { key: "plug", label: "Smart plug", status: d.plug.status, value: d.plug.inline, detail: d.plug.title },
      { key: "sl", label: "Starlink", status: d.sl.status, value: d.sl.inline, detail: d.sl.title },
      { key: "ups", label: "UPS / APC", status: d.ups.status, value: d.ups.inline, detail: d.ups.title },
      { key: "ts", label: "Tailscale", status: d.ts.status, value: d.ts.inline, detail: d.ts.title },
    ];

    // --- scanner (on-demand) ---
    const scanMins = minutesSince(scan?.scan_end_utc);
    out.push({
      key: "scanner",
      label: "RFID scanner",
      pending: scanLoading,
      status: scanLoading
        ? "unknown"
        : scanError
          ? "unknown"
          : freshnessStatus(scanMins, SCAN_WARN_MIN, SCAN_BAD_MIN),
      value: scanLoading
        ? "checking…"
        : scanError
          ? "no data"
          : scan?.scan_end_utc
            ? `last scan ${fmtAgo(scan.scan_end_utc)}`
            : "never scanned",
      detail: scanError
        ? `Scan detail unavailable: ${scanError}`
        : scan?.scan_end_utc
          ? [
              `Last scan ended ${fmtAgo(scan.scan_end_utc)} ago`,
              scan.total_tags != null ? `${scan.total_tags} tags` : null,
              scan.antennas?.length
                ? `antennas: ${scan.antennas
                    .map((a) => `${a.antenna_number}:${a.epc_count}`)
                    .join(" ")}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : "No scan on record for this box.",
    });

    // --- camera (on-demand) ---
    const photoMins = minutesSince(photo?.timestamp);
    out.push({
      key: "camera",
      label: "Camera",
      pending: photoLoading,
      status: photoLoading
        ? "unknown"
        : photoError
          ? "unknown"
          : freshnessStatus(photoMins, PHOTO_WARN_MIN, PHOTO_BAD_MIN),
      value: photoLoading
        ? "checking…"
        : photoError
          ? "no data"
          : photo?.timestamp
            ? `last photo ${fmtAgo(photo.timestamp)}`
            : "no photos",
      detail: photoError
        ? `Photos unavailable: ${photoError}`
        : photo?.timestamp
          ? [
              `Last photo ${fmtAgo(photo.timestamp)} ago`,
              photo.camera_name ? `camera ${photo.camera_name}` : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : "No photos on record for this box.",
    });

    return out;
  }, [box, scan, scanLoading, scanError, photo, photoLoading, photoError]);

  // A row with status null means "this box has no mapping for that source" —
  // hide it rather than showing a permanently gray line the tech can't act on.
  const visible = rows.filter((r) => r.status !== null);
  const problems = visible.filter((r) => r.status === "bad" || r.status === "warn");

  return (
    <div className="border border-secondary-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-secondary-50 border-b border-secondary-200 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Device status</h3>
        <span className="text-[11px] text-secondary-500">
          {problems.length === 0
            ? "all reporting normal"
            : `${problems.length} need${problems.length === 1 ? "s" : ""} attention`}
        </span>
      </div>

      <ul className="divide-y divide-secondary-100">
        {visible.map((r) => (
          <li
            key={r.key}
            // min-h keeps every row a comfortable tap target on a phone.
            className="px-3 py-2 min-h-[44px] flex items-start justify-between gap-3"
            title={r.detail}
          >
            <div className="flex items-center gap-2 shrink-0">
              <SignalDot label="" status={r.status} inline="" title={r.detail} />
              <span className="text-sm font-medium">{r.label}</span>
            </div>
            <div className="text-right min-w-0">
              {/* The status word, not just the dot colour: readable in
                  sunlight and by colour-blind users. */}
              <div
                className={
                  "text-sm tabular-nums " +
                  (r.status === "bad"
                    ? "text-error-700 font-semibold"
                    : r.status === "warn"
                      ? "text-warning-700 font-medium"
                      : r.status === "ok"
                        ? "text-secondary-700"
                        : "text-secondary-400")
                }
              >
                {r.pending ? "checking…" : r.value || "—"}
              </div>
              {/* Second line on mobile: the tooltip is unreachable on touch. */}
              {r.detail && (
                <div className="text-[11px] text-secondary-500 leading-snug break-words md:hidden">
                  {r.detail}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {visible.length === 0 && (
        <div className="px-3 py-3 text-sm text-secondary-500">
          No device signals are mapped for this box.
        </div>
      )}
    </div>
  );
};

export default DeviceStatusTile;
